// @US/AvatarComponent.tsx
"use client";

import { Avatar, AvatarFallback, AvatarImage, fmt } from "visuais";
import { useUserAvatarUrl, AvatarComponentProps } from "@US";
import { useUser } from "store";
import { useState } from "react";

const AvatarSkeleton = ({ className }: { className: string }) => (
  <div
    className={`${className} rounded-full relative overflow-hidden bg-muted`}
  >
    <div className="absolute inset-0 rounded-full animate-spin">
      <div className="h-full w-full rounded-full border-2 border-transparent border-t-primary border-r-primary/30"></div>
    </div>
    <div className="absolute inset-1 rounded-full bg-muted animate-pulse"></div>
  </div>
);

export function AvatarComponent({
  username: propUsername,
  className = "",
  showSkeleton = true,
  fallbackBg = "bg-primary",
}: AvatarComponentProps) {
  // Dados do usuário logado (apenas para fallback de nome/iniciais)
  const loggedUser = useUser();

  // Username final para busca do avatar
  const finalUsername = propUsername || loggedUser.login;
  const avatarQuery = useUserAvatarUrl(finalUsername || "");

  // Dados finais do usuário
  const finalUserInitials = finalUsername
    ? fmt.strings.initials(finalUsername)
    : loggedUser.userInitials;
  const finalUserDisplay = finalUsername || loggedUser.userDisplay;

  // URL do avatar sempre via hook local first
  const avatarUrl = avatarQuery.fileUrl;

  // Estados de loading
  const isLoading = avatarQuery.isLoading;

  // Mostra skeleton se necessário
  const shouldShowSkeleton = showSkeleton && isLoading;

  // Estado para forçar retry ao errar o carregamento da imagem
  const [retryCount, setRetryCount] = useState(0);

  // Função chamada ao erro de carregamento da imagem
  function handleImageError() {
    // Limite de tentativas para evitar loop infinito
    if (retryCount < 3) {
      setTimeout(() => setRetryCount((c) => c + 1), 1000); // tenta novamente após 1s
    }
  }

  if (shouldShowSkeleton) {
    return <AvatarSkeleton className={className} />;
  }

  // Se não tem nome para mostrar
  if (!finalUsername) {
    return (
      <Avatar className={className}>
        <AvatarFallback className={`${fallbackBg} font-semibold`}>
          ?
        </AvatarFallback>
      </Avatar>
    );
  }

  return (
    <Avatar className={className}>
      <AvatarImage
        key={retryCount}
        src={avatarUrl || undefined}
        alt={finalUserDisplay}
        onError={handleImageError}
      />
      <AvatarFallback className={`${fallbackBg} font-semibold`}>
        {finalUserInitials}
      </AvatarFallback>
    </Avatar>
  );
}
